import {
  type Actor,
  type Direction,
  type World,
  createActor,
  isWalkable,
  moveActor,
} from '@tama/game-core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { STEP_REPEAT_MS } from '../config';

/**
 * Where the pet is standing, and how a direction moves it.
 *
 * The actor lives in the client: with the world server-authoritative and the
 * pet unable to come to harm, a step has nothing to validate remotely and a
 * round trip per tile would make walking feel awful. It resets to the spawn
 * point whenever the map it is standing on changes shape.
 */
export function useActor(world: World | undefined) {
  const [actor, setActor] = useState<Actor | null>(null);

  // Re-checked on every version of the world, including each dev-mode edit:
  // painting a wall over the pet has to put it somewhere, and the spawn point is
  // the one tile the map guarantees it can stand on.
  useEffect(() => {
    if (!world) return;
    setActor((current) =>
      current && isWalkable(world, current.x, current.y) ? current : createActor(world),
    );
  }, [world]);

  const worldRef = useRef(world);
  worldRef.current = world;

  const step = useCallback((direction: Direction) => {
    const current = worldRef.current;
    if (!current) return;
    setActor((previous) => (previous ? moveActor(current, previous, direction).actor : previous));
  }, []);

  /** Puts the pet back on the spawn point — what dev mode's "reset" needs. */
  const respawn = useCallback(() => {
    const current = worldRef.current;
    if (current) setActor(createActor(current));
  }, []);

  return { actor, step, respawn };
}

/**
 * A held direction, repeating.
 *
 * The browser's own key repeat is not usable here: its initial delay is half a
 * second and its rate is a per-machine OS setting, so the pet would stall on
 * the first tile and then bolt. Native repeats are ignored and this drives its
 * own timer instead, which is also what lets the on-screen pad share the code.
 */
export function useHeldDirection(onStep: (direction: Direction) => void, enabled: boolean) {
  const held = useRef<Direction | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stepRef = useRef(onStep);
  stepRef.current = onStep;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const release = useCallback((direction?: Direction) => {
    // A different key going up must not cancel the one still held down.
    if (direction && held.current !== direction) return;
    held.current = null;
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const press = useCallback((direction: Direction) => {
    if (!enabledRef.current || held.current === direction) return;

    held.current = direction;
    stepRef.current(direction);

    if (timer.current !== null) clearInterval(timer.current);
    timer.current = setInterval(() => {
      if (held.current) stepRef.current(held.current);
    }, STEP_REPEAT_MS);
  }, []);

  // A timer that outlives the screen would keep walking a pet nobody is watching.
  useEffect(() => release, [release]);
  useEffect(() => {
    if (!enabled) release();
  }, [enabled, release]);

  return { press, release };
}

/** Arrow keys and WASD, the two things everyone tries first. */
export function directionForKey(key: string): Direction | null {
  switch (key) {
    case 'ArrowUp':
    case 'Up':
    case 'w':
    case 'W':
      return 'up';
    case 'ArrowDown':
    case 'Down':
    case 's':
    case 'S':
      return 'down';
    case 'ArrowLeft':
    case 'Left':
    case 'a':
    case 'A':
      return 'left';
    case 'ArrowRight':
    case 'Right':
    case 'd':
    case 'D':
      return 'right';
    default:
      return null;
  }
}
